import { classed } from "@/lib/classed";
import * as React from "react";
import { type DataTestIdProps, extractTestId } from "./data-test-id";

/**
 * Button Component
 * A versatile button component with multiple variants, sizes, and states
 */
const ButtonBase = classed("button", {
  base: "font-medium rounded-lg transition-colors focus:outline-none focus-visible:ring-1 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2",
  variants: {
    color: {
      primary: "",
      danger: "",
      success: "",
      neutral: "",
    },
    variant: {
      solid: "",
      outline: "",
      ghost:
        "text-[color:var(--on-surface)] hover:bg-[color:var(--surface-container-low)] focus-visible:ring-[color:var(--on-surface)]",
      link: "text-[color:var(--primary)] hover:text-[color:var(--on-primary-container)] hover:underline focus-visible:ring-[color:var(--primary)]",
    },
    size: {
      xs: "px-2 py-1 text-xs",
      sm: "px-3 py-1.5 text-sm",
      md: "px-4 py-2 text-sm",
      lg: "px-6 py-3 text-base",
      xl: "px-8 py-4 text-lg",
    },
    fullWidth: {
      true: "w-full",
    },
    loading: {
      true: "cursor-wait",
    },
  },
  defaultVariants: {
    color: "primary",
    variant: "solid",
    size: "md",
  },
  compoundVariants: [
    {
      color: "primary",
      variant: "solid",
      className:
        "bg-[color:var(--primary)] text-[color:var(--on-primary)] hover:bg-[color:var(--primary-hover)] focus-visible:ring-[color:var(--primary)] active:ring-[1.2px] active:ring-[color:var(--primary)]",
    },
    {
      color: "primary",
      variant: "outline",
      className:
        "border border-solid border-[color:var(--primary)] text-[color:var(--primary)] hover:bg-[color:var(--primary-container)] bg-transparent focus-visible:ring-[color:var(--primary)] active:ring-[1.2px] active:ring-[color:var(--primary)]",
    },
    {
      color: "danger",
      variant: "solid",
      className:
        "bg-[color:var(--error)] text-[color:var(--on-error)] hover:bg-[color:var(--error)] focus-visible:ring-[color:var(--error)] active:ring-[1.2px] active:ring-[color:var(--error)]",
    },
    {
      color: "danger",
      variant: "outline",
      className:
        "border border-solid border-[color:var(--error)] text-[color:var(--error)] hover:bg-[color:var(--error-container)] bg-transparent focus-visible:ring-[color:var(--error)] active:ring-[1.2px] active:ring-[color:var(--error)]",
    },
    {
      color: "success",
      variant: "solid",
      className:
        "bg-[color:var(--accent-green)] text-[color:var(--on-accent-green)] hover:bg-[color:var(--accent-green)] focus-visible:ring-[color:var(--accent-green)] active:ring-[1.2px] active:ring-[color:var(--accent-green)]",
    },
    {
      color: "success",
      variant: "outline",
      className:
        "border border-solid border-[color:var(--accent-green)] text-[color:var(--accent-green)] hover:bg-[color:var(--accent-green)]/10 bg-transparent focus-visible:ring-[color:var(--accent-green)] active:ring-[1.2px] active:ring-[color:var(--accent-green)]",
    },
    {
      color: "neutral",
      variant: "outline",
      className:
        "border border-solid border-[color:var(--outline)] text-[color:var(--on-surface)] hover:bg-[color:var(--surface-container-low)] bg-[color:var(--surface-container)] focus-visible:ring-[color:var(--outline)] active:ring-[1.2px] active:ring-[color:var(--outline)]",
    },
    {
      color: "neutral",
      variant: "solid",
      className:
        "bg-[color:var(--surface-container)] text-[color:var(--on-surface)] hover:bg-[color:var(--surface-container-low)] focus-visible:ring-[color:var(--outline)] active:ring-[1.2px] active:ring-[color:var(--outline)]",
    },
  ],
});

export type ButtonProps = React.ComponentPropsWithoutRef<typeof ButtonBase> &
  DataTestIdProps;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    const {
      ["data-testId"]: _,
      ["data-testid"]: __,
      ...rest
    } = props as DataTestIdProps & Record<string, unknown>;

    return (
      <ButtonBase ref={ref} data-testid={extractTestId(props)} {...rest} />
    );
  },
);
